import React, { useEffect, useState } from "react";
import ReactJson from "react-json-view";
import { Collapse } from "react-collapse";
import { t } from "i18next";

const ParameterDetailsTable = ({ data, isReview }) => {
    // Table states
    const [headers, setHeaders] = useState([]);
    const [tableData, setTableData] = useState([]);
    // Collapse states
    const [isCollapsed, setIsCollapsed] = useState({});

    useEffect(() => {
        // Set table states only if not in review mode
        if (!isReview) {
            const mappedData = mapData();
            setHeaders(Object.keys(mappedData[0]));
            setTableData(mappedData);
        }
    }, []);

    const mapData = () => {
        return data.map((v, index) => ({
            parameterName: getParameter("parameterName", index),
            r2: getParameterWithDecimalPlaces("R2", index),
            rmse: getParameterWithDecimalPlaces("RMSE", index),
            range: getRangeValue(index),
            numberOfSamples: getParameter("numberOfSamples", index),
        }));
    };

    // Get parameter from object
    const getParameter = (child, index, fallback = "--") => {
        return data && (data[index][child] || fallback);
    };

    // Get parameter from object with 3 decimal places
    const getParameterWithDecimalPlaces = (child, index) => {
        const parameter = getParameter(child, index);
        return +parameter ? Number(parameter).toFixed(3) : parameter;
    };

    const getRangeValue = (index) => {
        const minValue = getParameter("minValue", index, null);
        const maxValue = getParameter("maxValue", index, null);
        const unit = getParameter("unit", index);

        if (maxValue >= 1000 || (!minValue && !maxValue)) return `-- ${unit}`;
        return `${minValue} - ${maxValue} ${unit}`;
    };

    // Renders JSON formatter
    const renderOperations = (index) => {
        //Making sure model info values are of maximum 3 decimal places
        Object.keys(data[index].modelInfo).map((key) => {
            const value = data[index].modelInfo[key];
            data[index].modelInfo[key] =
                typeof value === "number" ? +Number(value).toFixed(3) : value;
        });

        const obj = {
            [t("numberOfMeasurements")]: data[index].numberOfMeasurements,
            [t("modelInfo")]: data[index].modelInfo,
            [t("operations")]: data[index].operations,
            [t("operationScreening")]: data[index].operationScreening,
        };
        return (
            <ReactJson
                src={obj}
                name={false}
                displayDataTypes={false}
                collapsed={1}
                enableClipboard={false}
                style={{
                    fontSize: 12,
                    padding: 14,
                    width: "100%",
                    textAlign: "left",
                }}
                indentWidth={4}
            />
        );
    };

    const handleExpand = (key) => {
        let newCollapse = { ...isCollapsed };
        newCollapse[key] = !isCollapsed[key];
        setIsCollapsed(newCollapse);
    };

    const renderHeaders = () => {
        return (
            <>
                {headers.map((elem, index) => (
                    <td key={index}>
                        <div className="font-bold px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                            <div className="flex items-center justify-center">
                                {t(elem)}
                            </div>
                        </div>
                    </td>
                ))}
            </>
        );
    };

    const renderTable = () => {
        return (
            <div className=" table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center w-full">
                    <thead className="parent-head">
                        <tr>{renderHeaders()}</tr>
                    </thead>
                    <tbody>
                        {data &&
                            tableData.map((elem, keyIndex) => (
                                <tr className="tr-ele" key={keyIndex}>
                                    {[...Object.values(elem)].map(
                                        (row, index) => (
                                            <td
                                                className="px-6 py-3"
                                                key={`${keyIndex}-${index}`}
                                            >
                                                <span
                                                    className={`${
                                                        index === 0 &&
                                                        "text-primary-blue font-medium text-tiny"
                                                    }`}
                                                >
                                                    {row}
                                                </span>
                                            </td>
                                        )
                                    )}
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderCollapsableParams = () => {
        return (
            data &&
            data.map((row, index) => (
                <div key={index}>
                    <div
                        className="text-sm"
                        onClick={() => handleExpand(row.parameterName)}
                    >
                        <div className="px-6 py-3 whitespace-wrap">
                            <div className="flex items-center">
                                <i
                                    className={`fas fa-caret-${
                                        isCollapsed[row.parameterName]
                                            ? "down"
                                            : "right"
                                    } mr-2 color-blue`}
                                />
                                <span className="text-left color-blue font-semibold truncate">
                                    {row.parameterName}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="collapse-animation">
                        <Collapse isOpened={isCollapsed[row.parameterName]}>
                            <div className="px-8 pb-4 border-solid border-b-2 border-primary-color-blue">
                                {renderOperations(index)}
                            </div>
                        </Collapse>
                    </div>
                </div>
            ))
        );
    };

    // Renders Collapsable JSON formatter when in review mode otherwise a param table
    const renderContent = () => {
        if (isReview)
            return (
                <div className="card-container">
                    {renderCollapsableParams()}
                </div>
            );
        return renderTable();
    };

    return renderContent();
};

export default ParameterDetailsTable;
